"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import Image from "next/image";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import placeholderImage from "@/assets/images/common/1x1-00000000.png";
import LeftArrowIcon from "@/assets/svg/left-arrow.svg";
import RightArrowIcon from "@/assets/svg/right-arrow.svg";
import { PopLoading } from "@/components/pop-ui/pop-loading";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn, formatPrice, getCurrencySymbol, processTranslations } from "@/lib/utils";
import { getCurrentAreaInClient, getCurrentLangInClient, useLocaleRouter } from "@/lib/utils.client";
import { useORPC } from "@/providers/orpc-provider";

// 商品标签枚举
export enum ProductTagEnum {
  LIMITED_VOLUME = "LIMITED_VOLUME",
  // 限定品
  NEW = "NEW",
  // 新品
  HOT = "HOT",
  // 热品
  SOLD_OUT = "SOLD_OUT",
  // 售罄
  OUT_OF_STOCK = "OUT_OF_STOCK",
  // 缺货
  PRE_ORDER = "PRE_ORDER",
  // 预售
  COMING_SOON = "COMING_SOON" // 即将开售
  ,
}
interface GuessYouLikeSectionProps {
  q: string;
  id: string;
}

// 标签优先级处理
function renderTags(tags: string[] = [], t: any) {
  const styledTagElements: React.ReactNode[] = [];

  /* 只展示缺货 */
  if (tags.includes(ProductTagEnum.OUT_OF_STOCK)) {
    styledTagElements.push(<span key="sold-out" className="flex h-[calc(26/750*100vw)] items-center bg-[#DDDDDD] px-[calc(6/750*100vw)] py-1 font-['DINPro-Regular'] text-[calc(18/750*100vw)] leading-[calc(18/750*100vw)] font-normal whitespace-nowrap text-black uppercase md:h-auto md:px-3 md:text-[min(calc(12/1920*100vw),12px)] md:leading-[min(calc(12/1920*100vw),12px)]">
				{t("product.tags.outOfStock")}
			</span>);
    return styledTagElements;
  }
  return styledTagElements;
}

// 定义商品类型接口
interface ProductItem {
  id: string;
  mainImage?: string | null;
  name: string;
  tags?: string[] | null;
  brand?: string | null;
  areaSkuPrices?: {
    [areaCode: string]: {
      price: number;
      currency: string;
    };
  };
  bannerImages?: any[];
}
interface ProductItemCardProps {
  item: ProductItem;
  onClick: () => void;
}

// 商品卡片组件
const ProductItemCard: React.FC<ProductItemCardProps> = t0 => {
  const $ = _c(40);
  const {
    item,
    onClick
  } = t0;
  const {
    t
  } = useTranslation();
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const currentArea = getCurrentAreaInClient();
  const areaPriceInfo = item.areaSkuPrices?.[currentArea];
  const currency = areaPriceInfo?.currency || "USD";
  const price = areaPriceInfo?.price || 0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      paddingBottom: "125%"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== item.tags) {
    t2 = item.tags || [];
    $[1] = item.tags;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t || $[4] !== t2) {
    t3 = renderTags(t2, t);
    $[3] = t;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <div className="absolute top-2 left-2 z-10 flex flex-wrap gap-[1px]">{t3}</div>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== isImageLoaded) {
    t5 = !isImageLoaded && <div className="absolute inset-0 z-20 flex items-center justify-center bg-[#F5F5F5]"><PopLoading loading={true} /></div>;
    $[8] = isImageLoaded;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = item.mainImage || placeholderImage.src;
  const t7 = item.name || "";
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      objectFit: "contain"
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const t9 = isImageLoaded ? "opacity-100" : "opacity-0";
  let t10;
  if ($[11] !== t9) {
    t10 = cn("h-full rounded transition-all duration-500 ease-out group-hover:scale-105", t9);
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  let t12;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setIsImageLoaded(true);
    t12 = e => {
      setIsImageLoaded(true);
      (e.target as HTMLImageElement).src = placeholderImage.src;
    };
    $[13] = t11;
    $[14] = t12;
  } else {
    t11 = $[13];
    t12 = $[14];
  }
  let t13;
  if ($[15] !== t10 || $[16] !== t6 || $[17] !== t7) {
    t13 = <Image src={t6} alt={t7} fill={true} loading="lazy" style={t8} className={t10} onLoad={t11} onError={t12} />;
    $[15] = t10;
    $[16] = t6;
    $[17] = t7;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t13 || $[20] !== t4 || $[21] !== t5) {
    t14 = <div className="h-42 relative w-full  overflow-hidden rounded bg-[#F5F5F5]" style={t1}>{t4}{t5}{t13}</div>;
    $[19] = t13;
    $[20] = t4;
    $[21] = t5;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== item.name) {
    t15 = <div className="mb-3 line-clamp-2 font-['DINPro-Regular'] text-xs transition-colors  md:mb-1 md:font-['DINPro-Medium'] md:text-sm md:font-medium">{item.name}</div>;
    $[23] = item.name;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== currency) {
    t16 = getCurrencySymbol(currency);
    $[25] = currency;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== currency || $[28] !== price) {
    t17 = formatPrice(currency, price);
    $[27] = currency;
    $[28] = price;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t16 || $[31] !== t17) {
    t18 = <div className="mt-auto font-['DINPro-Regular'] text-xs text-black md:font-['DINPro-Bold'] md:text-base md:font-bold">{t16}{t17}</div>;
    $[30] = t16;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t15 || $[34] !== t18) {
    t19 = <div className="mt-2 flex flex-1 flex-col">{t15}{t18}</div>;
    $[33] = t15;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== onClick || $[37] !== t14 || $[38] !== t19) {
    t20 = <div className="group flex h-full cursor-pointer flex-col bg-white transition" onClick={onClick}>{t14}{t19}</div>;
    $[36] = onClick;
    $[37] = t14;
    $[38] = t19;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  return t20;
};

// 骨架屏卡片组件
const PAGE_SIZE = 4;
const MAX_ITEMS = 16;
export const GuessYouLikeSection = t0 => {
  const $ = _c(47);
  const {
    q,
    id
  } = t0;
  const [page, setPage] = useState(1);
  const orpc = useORPC();
  const router = useLocaleRouter();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getCurrentLangInClient();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const lang = t1;
  const {
    t
  } = useTranslation();
  let t2;
  if ($[1] !== id || $[2] !== orpc.search.public_guess_like || $[3] !== q) {
    t2 = orpc.search.public_guess_like.queryOptions({
      input: {
        id,
        q,
        page: 1,
        pageSize: MAX_ITEMS
      },
      queryKey: ["guess_like", q],
      enabled: !!id && !!q
    });
    $[1] = id;
    $[2] = orpc.search.public_guess_like;
    $[3] = q;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = data => {
      const processedData = processTranslations(data, lang);
      return {
        ...processedData,
        items: processedData.items.map(_temp)
      };
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2) {
    t4 = {
      ...t2,
      select: t3
    };
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    data: data_0,
    isLoading
  } = useQuery(t4);
  let t5;
  if ($[8] !== data_0?.items) {
    t5 = data_0?.items || [];
    $[8] = data_0?.items;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const allItems = t5;
  const isMobile = useIsMobile();
  const startIndex = (page - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  let t6;
  if ($[10] !== allItems || $[11] !== endIndex || $[12] !== isMobile || $[13] !== startIndex) {
    t6 = isMobile ? allItems : allItems.slice(startIndex, endIndex);
    $[10] = allItems;
    $[11] = endIndex;
    $[12] = isMobile;
    $[13] = startIndex;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const currentPageItems = t6;
  const totalPages = Math.ceil(allItems.length / PAGE_SIZE);
  const isFirstPage = page <= 1;
  const isLastPage = page >= totalPages;
  if (!q) {
    return null;
  }
  if (!isLoading && currentPageItems.length === 0) {
    return null;
  }
  let t7;
  if ($[15] !== t) {
    t7 = t("product.guessYouLike");
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t7) {
    t8 = <h2 className="md:text-3xl text-lg font-bold font-['DINPro-Bold'] px-3 md:px-0">{t7}</h2>;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== allItems.length || $[20] !== page || $[21] !== totalPages) {
    t9 = allItems.length > PAGE_SIZE && <p className="text-base font-medium font-['DINPro-Medium'] hidden md:inline">{page}/{totalPages}</p>;
    $[19] = allItems.length;
    $[20] = page;
    $[21] = totalPages;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t8 || $[24] !== t9) {
    t10 = <div className="flex justify-between items-end md:mb-6 mb-3">{t8}{t9}</div>;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== currentPageItems || $[27] !== router) {
    let t12;
    if ($[29] !== router) {
      t12 = item_0 => <div key={item_0.id} className="flex-shrink-0 w-2/5"><ProductItemCard item={item_0} onClick={() => router.push(`/products/${item_0.id}`)} /></div>;
      $[29] = router;
      $[30] = t12;
    } else {
      t12 = $[30];
    }
    t11 = currentPageItems.map(t12);
    $[26] = currentPageItems;
    $[27] = router;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[31] !== t11) {
    t12 = <div className="flex px-3 pb-4 gap-2 md:gap-4 w-full overflow-x-auto md:hidden">{t11}</div>;
    $[31] = t11;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== currentPageItems || $[34] !== isFirstPage || $[35] !== isLastPage || $[36] !== isLoading || $[37] !== router || $[38] !== t || $[39] !== totalPages) {
    t13 = currentPageItems.length > 0 ? <div className="relative w-full"><div className="grid grid-cols-4 gap-3">{currentPageItems.map(item_1 => <ProductItemCard key={item_1.id} item={item_1} onClick={() => router.push(`/products/${item_1.id}`)} />)}</div>{totalPages > 1 && <><button type="button" disabled={isFirstPage} onClick={() => !isFirstPage && setPage(_temp2)} className={cn("absolute -left-6 z-10 cursor-pointer bg-transparent border-0 p-0")} style={{
          top: "37.5%"
        }}><LeftArrowIcon /></button><button type="button" disabled={isLastPage} onClick={() => !isLastPage && setPage(p_0 => Math.min(totalPages, p_0 + 1))} className={cn("absolute -right-6 z-10 cursor-pointer bg-transparent border-0 p-0")} style={{
          top: "37.5%"
        }}><RightArrowIcon /></button></>}</div> : !isLoading && <div className="text-center py-4 text-gray-500 font-['DINPro-Regular']">{t("product.noMoreRecommendations")}</div>;
    $[33] = currentPageItems;
    $[34] = isFirstPage;
    $[35] = isLastPage;
    $[36] = isLoading;
    $[37] = router;
    $[38] = t;
    $[39] = totalPages;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== t13) {
    t14 = <div className="hidden md:block">{t13}</div>;
    $[41] = t13;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== t10 || $[44] !== t12 || $[45] !== t14) {
    t15 = <div className="mx-auto py-8  md:mt-8">{t10}{t12}{t14}</div>;
    $[43] = t10;
    $[44] = t12;
    $[45] = t14;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  return t15;
};
export default GuessYouLikeSection;
function _temp(item) {
  const mainImage = item.bannerImages?.[0]?.type === "image" ? item.bannerImages?.[0]?.link : item.bannerImages?.[0]?.coverImage;
  return {
    ...item,
    mainImage
  };
}
function _temp2(p) {
  return Math.max(1, p - 1);
}